import { pick, identity, pickBy } from 'lodash';
import {
  all, call, put, select, takeEvery, takeLatest,
} from 'redux-saga/effects';
import callAdapter, { callAdapterResponseError } from '@/utils/sagaHelper';
import findResult, {
  findWithCodes,
  findWithReadStatus,
  isValidFilter,
  successAndErrors,
  successAndErrorsOnDestroy,
  wildcardFilter,
} from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import { CARDS_PAGE_SIZE, presentationOptions } from '@/constants/states';
import selectors from '@/selectors';
import { findWithUUIDs } from '@/utils/uuidHelper';
import {
  userGroupLists,
  userUsergroupsActions,
  usergroupUsersActions,
  readUsergroupActions,
  createUsergroupActions,
  updateUsergroupActions,
  deleteUsergroupActions,
} from '../constants';
import UserGroupsService from '../service';
import { createUserGroupError, createUserGroupSuccess } from '../actions/usergroups/create';
import {
  showUserGroup,
  showUserGroupError,
  showUserGroups,
  showUserGroupsError,
  fetchUserGroupsMetrics,
  showClonedUserGroup,
  showUserGroupsMetrics,
  showUserGroupsMetricsError,
  showUsergroupSearchUsers,
  showUsergroupSearchUsersError,
} from '../actions/usergroups/read';
import { updateUserGroupError, updateUserGroupSuccess } from '../actions/usergroups/update';
import { deleteUserGroupError, deleteUserGroupSuccess } from '../actions/usergroups/delete';
import {
  addUsergroupUsersError,
  addUsergroupUsersLocal,
  addUsergroupUsersSuccess,
  removeUsergroupUserError,
  removeUsergroupUserLocal,
  removeUsergroupUserSuccess,
  showUserGroupUsers,
  showUserGroupUsersError,
} from '../actions/usergroupUsers';
import {
  addUsersGroupsError,
  addUsersGroupsSuccess,
  removeUsersGroupError,
  removeUsersGroupSuccess,
  showFilteredGroups,
  showFilteredGroupsError,
  showUsersGroups,
  showUsersGroupsError,
  addUsersGroupsLocal,
  removeUsersGroupLocal,
} from '../actions/userUsergroups';
import UsersService from '../../service';
import { refreshUsersMetrics } from '../../actions';

function* fetchUserGroups(action) {
  const items = yield select(selectors.userGroups);

  const {
    bpmn, display, filter, pageSize, currentPage, orderBy,
  } = items[action.category];

  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const searchTerm = action.filter || filter;

  const { response, error } = yield call(
    callAdapter,
    UserGroupsService.userGroups(
      wildcardFilter(searchTerm),
      page,
      display === presentationOptions.CARDS ? CARDS_PAGE_SIZE : size,
      orderBy,
      bpmn,
    ),
  );
  return { response, error };
}

export function* userGroups(action) {
  const items = yield select(selectors.userGroups);
  const { pageSize } = items[action.category];
  const size = action.pageSize || pageSize;

  const { response, error } = yield fetchUserGroups(action);

  if (error) {
    yield put(showUserGroupsError(action.category, error));
  } else {
    yield put(showUserGroups(
      action.category,
      action.append,
      size,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
    ));
  }
}

export function* userGroupsMetrics(action) {
  const { response, error } = yield fetchUserGroups(action);

  if (error) {
    yield put(showUserGroupsMetricsError(action.category, error));
  } else {
    yield put(showUserGroupsMetrics(
      action.category,
      response.items,
    ));
  }
}

export function* userGroupUsers({
  category, code, page, pageSize,
}) {
  const { response, error } = yield call(
    callAdapter,
    UserGroupsService.userGroupUsers(code, page, pageSize),
  );
  if (error) {
    yield put(showUserGroupUsersError(category, code, error));
  } else {
    yield put(showUserGroupUsers(category, code, response.items));
  }
}

export function* fetchUserGroup({ category, code, isNew }) {
  if (isNew) {
    const now = Date.now();
    const newResult = {
      code,
      created_at: now,
      updated_at: now,
    };

    yield put(showUserGroup(category, code, newResult, true));
    return;
  }

  const { response, error } = yield call(callAdapter, UserGroupsService.userGroup(code));
  if (error) {
    yield put(showUserGroupError(category, code, error));
  } else {
    yield put(showUserGroup(category, code, response));
  }
}

function* assignUsersToUserGroup(code, activeUserGroup) {
  const users = activeUserGroup.users.results.map((user) => ({ uuid: user.uuid }));
  const { response, error } = yield call(
    callAdapter,
    UserGroupsService.assignUsersToGroup(code, { users }),
  );

  if (error) {
    yield put(createUserGroupError(error));
  }

  return { response, error };
}

export function* createUserGroup() {
  const activeUserGroup = yield select(selectors.activeUserGroup);

  const { response, error } = yield call(
    callAdapter,
    UserGroupsService.createGroup(
      pickBy(pick(activeUserGroup, ['code', 'name', 'description', 'bpmn']), identity),
    ),
  );

  if (error) {
    yield put(createUserGroupError(error));
    return;
  }

  if (activeUserGroup.userGroupSourceCode && activeUserGroup.users.results.length) {
    const { error: errorAssign } = yield assignUsersToUserGroup(response.code, activeUserGroup);
    if (errorAssign) {
      return;
    }
  }

  const items = yield select(selectors.userGroups);
  const toRefresh = findWithReadStatus(items, userGroupLists, states.SUCCESS);

  yield put(createUserGroupSuccess('open', activeUserGroup.uuid, activeUserGroup.userGroupSourceCode, response));
  yield all(toRefresh.map((category) => userGroups({ category })));
}

function* cloneUserGroup(category, code, responseUserGroup) {
  const { response, error } = yield call(
    callAdapter,
    UserGroupsService.userGroupUsers(responseUserGroup.code, 1, 1000),
  );

  if (error) {
    yield put(showUserGroupError(category, code, error));
  } else {
    const filterUsers = response.items.filter(({ archived }) => !archived);
    yield put(showClonedUserGroup(category, code, responseUserGroup, filterUsers));
  }
}

export function* newUserGroupFromSource({ category, code, userGroupSourceCode }) {
  const userGroupCode = decodeURIComponent(userGroupSourceCode);
  const { response, error } = yield call(callAdapter, UserGroupsService.userGroup(userGroupCode));

  if (error) {
    yield put(showUserGroupError(category, code, error));
  } else {
    yield call(cloneUserGroup, category, code, response);
  }
}

export function* updateUserGroup({ category, code }) {
  const group = findResult(yield select(selectors.userGroups), category, code);

  const { response, error } = yield call(callAdapter, UserGroupsService.updateUserGroup(code, group));

  if (error) {
    yield put(updateUserGroupError(category, code, error));
  } else {
    yield put(updateUserGroupSuccess(category, code, response));
  }
}

export function* destroy({ items }) {
  const results = yield all(
    items.map((i) => callAdapterResponseError(i, UserGroupsService.destroyGroup(i))),
  );
  const { success, errors } = successAndErrorsOnDestroy(results, 'User group not found');
  if (errors.length > 0) {
    yield put(deleteUserGroupError(errors));
  }
  if (success.length > 0) {
    const userGroupItems = yield select(selectors.userGroups);
    const codes = success.map((item) => item.code);
    const categories = findWithCodes(userGroupItems, userGroupLists, codes);
    const toRefresh = findWithReadStatus(userGroupItems, categories, states.SUCCESS);

    yield put(deleteUserGroupSuccess(success, false));
    if (toRefresh) {
      yield all(toRefresh.map((category) => userGroups({ category })));
    }
  }
}

export function* refreshUserGroupsPresentation(action) {
  yield call(userGroups, { ...action, page: 1, append: false });
}

export function* fetchGroups(action) {
  const items = yield select(selectors.userGroups);

  const {
    filter, pageSize, currentPage, orderBy,
  } = items[action.category];

  const page = action.page || currentPage;
  const size = action.pageSize || pageSize;
  const searchTerm = action.filter || filter;
  const requiresFilter = action.requiresFilter || false;
  const minFilterLength = action.minFilterLength || 0;

  if (!isValidFilter(searchTerm, { requiresFilter, minFilterLength })) { return; }

  const { response, error } = yield call(
    callAdapter,
    UserGroupsService.userGroups(
      wildcardFilter(searchTerm),
      page,
      size,
      orderBy,
    ),
  );

  if (error) {
    yield put(showFilteredGroupsError(action.category, action.user, error));
  } else {
    yield put(showFilteredGroups(
      action.category,
      action.user,
      response.items,
    ));
  }
}

export function* usersGroups({
  user, page, pageSize, filter, category,
}) {
  const { response, error } = yield call(
    callAdapter,
    UserGroupsService.usersUserGroups(user, page, pageSize, wildcardFilter(filter)),
  );

  if (error) {
    yield put(showUsersGroupsError(category, user, error));
  } else {
    yield put(showUsersGroups(
      category,
      user,
      response.items,
      response.paging.total,
      response.paging.pages,
      response.paging.page,
    ));
  }
}

export function* refreshTouchedUsersMetrics(users) {
  const usersState = yield select(selectors.users);

  const categoriesToRefresh = usersState ? findWithUUIDs(
    usersState,
    ['all'],
    users.map((user) => user.uuid),
  ) : [];

  if (categoriesToRefresh.length > 0) {
    yield all(
      categoriesToRefresh.map(
        (category) => put(refreshUsersMetrics(category)),
      ),
    );
  }
}

export function* refreshTouchedUsergroupMetrics(groups) {
  const usergroups = yield select(selectors.userGroups);

  const categoriesToRefresh = findWithCodes(
    usergroups,
    ['all'],
    groups.map((group) => group.code),
  );

  if (categoriesToRefresh.length > 0) {
    yield all(
      categoriesToRefresh.map((category) => put(fetchUserGroupsMetrics(category))),
    );
  }
}

export function* removeUsergroupFromUserLocal(user, group) {
  const usergroups = yield select(selectors.userGroups);
  const storeUser = usergroups?.assign?.users?.results?.find((u) => u.uuid === user.uuid);

  const storeUsergroup = findResult(usergroups, 'open', group.code);

  if (storeUser && storeUsergroup) {
    yield put(removeUsersGroupLocal('assign', storeUser, storeUsergroup));
  }
}

export function* removeUserFromUsergroupLocal(user, group) {
  const usergroups = yield select(selectors.userGroups);
  const storeUser = usergroups?.assign?.users?.results?.find((u) => u.uuid === user.uuid);

  const storeUsergroup = findResult(usergroups, 'open', group.code);

  if (storeUser && storeUsergroup) {
    yield put(removeUsergroupUserLocal('open', storeUsergroup, storeUser));
  }
}

export function* addUsergroupsToUsersLocal(users, groups) {
  const usergroups = yield select(selectors.userGroups);

  const userUsergroupPairs = users?.map((user) => groups.map((group) => {
    const storeUser = usergroups?.assign?.users?.results?.find((u) => u.uuid === user.uuid);

    const storeUsergroup = findResult(usergroups, 'open', group.code);

    if (storeUser && storeUsergroup) {
      return { storeUser, storeUsergroup };
    }
    return undefined;
  })).flat().filter(Boolean);

  yield all(
    userUsergroupPairs.map(({ storeUser, storeUsergroup }) => all([
      put(addUsersGroupsLocal('assign', storeUser, [storeUsergroup])),
    ])),
  );
}

export function* addUsersToUsergroupsLocal(users, groups) {
  const usergroups = yield select(selectors.userGroups);

  const userUsergroupPairs = users.map((user) => groups.map((group) => {
    const storeUser = usergroups?.assign?.users?.results?.find((u) => u.uuid === user.uuid);

    const storeUsergroup = findResult(usergroups, 'open', group.code);

    if (storeUser && storeUsergroup) {
      return { storeUser, storeUsergroup };
    }
    return undefined;
  })).flat().filter(Boolean);

  yield all(
    userUsergroupPairs.map(({ storeUser, storeUsergroup }) => all([
      put(addUsergroupUsersLocal('open', storeUsergroup, [storeUser])),
    ])),
  );
}

export function* destroyUsersGroup({ category, user, group }) {
  const { error } = yield call(callAdapter, UserGroupsService.removeUserFromGroup(group.code, user.uuid));

  if (error) {
    yield put(removeUsersGroupError(category, user, group, error));
    return;
  }

  yield put(removeUsersGroupSuccess(category, user, group));
  yield refreshTouchedUsergroupMetrics([group]);
  yield refreshTouchedUsersMetrics([user]);
  yield removeUserFromUsergroupLocal(user, group);
}

export function* addUsersGroups({ category, user, groups }) {
  const results = yield all(
    groups.map((i) => callAdapterResponseError(i, UserGroupsService.addUserToGroup(i.code, user.uuid))),
  );

  const { errors } = successAndErrors(results);

  if (errors.length) {
    yield put(addUsersGroupsError(category, user, groups, errors));
    return;
  }

  yield put(addUsersGroupsSuccess(category, user, groups));
  yield refreshTouchedUsergroupMetrics(groups);
  yield refreshTouchedUsersMetrics([user]);
  yield addUsersToUsergroupsLocal([user], groups);
}

export function* addUsersToUserGroup({ category, code }) {
  const group = findResult(yield select(selectors.userGroups), category, code);

  const results = yield all(
    group.users.toAdd.map((user) => callAdapterResponseError(
      user,
      UserGroupsService.addUserToGroup(group.code, user.uuid),
    )),
  );

  const { errors } = successAndErrors(results);

  if (errors.length > 0) {
    yield put(addUsergroupUsersError(category, code, group, errors));
    return;
  }

  yield put(addUsergroupUsersSuccess(category, code, group, results));
  yield refreshTouchedUsergroupMetrics([group]);
  yield refreshTouchedUsersMetrics(results);
  yield addUsergroupsToUsersLocal(results, [group]);
}

export function* removeUserFromUserGroup({ category, code, userId }) {
  const { error } = yield call(
    callAdapter,
    UserGroupsService.removeUserFromGroup(
      code,
      userId,
    ),
  );
  const group = findResult(yield select(selectors.userGroups), category, code);
  const user = group.users.results.find((u) => u.id === userId);

  if (error) {
    yield put(removeUsergroupUserError(category, code, group, user, error));
    return;
  }

  yield put(removeUsergroupUserSuccess(category, code, group, user));
  if (user) {
    yield refreshTouchedUsergroupMetrics([group]);
    yield refreshTouchedUsersMetrics([user]);
    yield removeUsergroupFromUserLocal(user, group);
  }
}

export function* searchUsergroupUsers({ category, code, ...action }) {
  const group = findResult(yield select(selectors.userGroups), category, code);
  const filter = action.filter || group.userSearch.filter;

  if (!isValidFilter(filter, { requiresFilter: true, minFilterLength: 3 })) {
    return;
  }

  const { response, error } = yield call(
    callAdapter,
    UsersService.users(
      wildcardFilter(filter),
      undefined,
      true,
      true,
      false,
      1,
      100,
    ),
  );

  if (error) {
    yield put(showUsergroupSearchUsersError(category, code, error));
  } else {
    yield put(showUsergroupSearchUsers(category, code, response.items));
  }
}

export default function* userGroupsApi() {
  yield takeEvery(readUsergroupActions.FETCH_USERGROUPS, userGroups);
  yield takeEvery(readUsergroupActions.FETCH_USERGROUPS_METRICS, userGroupsMetrics);
  yield takeEvery(readUsergroupActions.FETCH_USERGROUP, fetchUserGroup);
  yield takeEvery(readUsergroupActions.FETCH_USERGROUP_USERS_SEARCH, searchUsergroupUsers);
  yield takeLatest(createUsergroupActions.CREATE_USERGROUP, createUserGroup);
  yield takeEvery(createUsergroupActions.NEW_USERGROUP_FROM_SOURCE, newUserGroupFromSource);
  yield takeLatest(updateUsergroupActions.UPDATE_USERGROUP, updateUserGroup);
  yield takeLatest(deleteUsergroupActions.DELETE_USERGROUP, destroy);
  yield takeEvery(readUsergroupActions.TOGGLE_USERGROUPS_PRESENTATION, refreshUserGroupsPresentation);
  yield takeEvery(userUsergroupsActions.FETCH_FILTERED_GROUPS, fetchGroups);
  yield takeEvery(userUsergroupsActions.FETCH_USERS_GROUPS, usersGroups);
  yield takeEvery(userUsergroupsActions.REMOVE_USERS_GROUP, destroyUsersGroup);
  yield takeEvery(userUsergroupsActions.ADD_USERS_GROUPS, addUsersGroups);
  yield takeEvery(usergroupUsersActions.FETCH_USERGROUP_USERS, userGroupUsers);
  yield takeLatest(usergroupUsersActions.ADD_USERGROUP_USERS, addUsersToUserGroup);
  yield takeEvery(usergroupUsersActions.REMOVE_USERGROUP_USER, removeUserFromUserGroup);
}
